import { axiosKK } from './networkRequest';

/**
 * HubSpot's three assignment dropdowns, as `GET /admin/hubspot/options` serves
 * them. Each is empty when HubSpot could not be asked, and a picker built from
 * them then falls back to typing the name.
 */
export interface HubspotOptions {
  courier: string[];
  return_courier: string[];
  warehouse: string[];
}

const NO_OPTIONS: HubspotOptions = { courier: [], return_courier: [], warehouse: [] };

/** Resolves with empty lists rather than rejecting: a missing list is a fallback, not an error. */
export function fetchHubspotOptions(): Promise<HubspotOptions> {
  return axiosKK
    .get<{ options?: Partial<HubspotOptions> }>('/admin/hubspot/options')
    .then(({ data }) => ({ ...NO_OPTIONS, ...(data.options || {}) }))
    .catch(() => NO_OPTIONS);
}

/** The options a partner can be linked to: Collection Courier and Return Courier together. */
export function partnerOptions(options: HubspotOptions): string[] {
  const seen = new Map<string, string>();
  [...options.courier, ...options.return_courier].forEach((o) => {
    const k = hubspotKey(o);
    if (k && !seen.has(k)) seen.set(k, o);
  });
  return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
}

/**
 * What two HubSpot values are compared by, as the API compares them
 * (storage-api `HubspotLinks.key`): case and runs of whitespace never tell
 * them apart, and blank or the literal "null" is no value.
 */
export function hubspotKey(value: string | null | undefined): string | null {
  const v = (value || '').trim();
  if (!v || v.toLowerCase() === 'null') return null;
  return v.replace(/\s+/g, ' ').toLowerCase();
}

/** Whether something here is linked to the HubSpot value [raw]. */
export function isLinked(raw: string | null | undefined, linkedNames: Array<string | null | undefined>): boolean {
  const k = hubspotKey(raw);
  return k != null && linkedNames.some((n) => hubspotKey(n) === k);
}

/** The line an order shows while a change has not reached HubSpot; null once it has. */
export function syncStatusText(sync?: string | null, error?: string | null): string | null {
  if (sync === 'PENDING') return 'Waiting to reach HubSpot. It is retried every 15 minutes.';
  if (sync === 'FAILED') {
    const why = error && error.length > 160 ? `${error.slice(0, 157)}…` : error;
    return `Did not reach HubSpot${why ? ` (${why})` : ''}. Save the change again to resend it, or make it in HubSpot.`;
  }
  return null;
}
